import { useMemo, useState } from 'react';
import { AlertTriangle, Plus, Route, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import Swal from 'sweetalert2';
import PageHeaderCompact from '@/components/common/PageHeaderCompact.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import LimitChips from '@/components/common/LimitChips.jsx';
import MetricCardCompact from '@/components/common/MetricCardCompact.jsx';
import StatusChip from '@/components/common/StatusChip.jsx';
import { useAuth } from '@/state/auth/AuthContext.jsx';
import {
  ACTUALIZAR_LINEA_MUTATION,
  ELIMINAR_LINEA_MUTATION,
  LINEAS_QUERY,
  SET_LINEA_ACTIVA_MUTATION,
} from '@/graphql/lineas.gql.js';
import LineaForm from './LineaForm.jsx';

const formatNumber = value =>
  new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

export default function ListadoLineas() {
  const { sucursal } = useAuth();
  const [fallbackSucursalId, setFallbackSucursalId] = useState('');
  const [editing, setEditing] = useState(null);
  const sucursalId = Number(sucursal?.id || fallbackSucursalId || 0);

  const [result, reexecute] = useQuery({
    query: LINEAS_QUERY,
    variables: { sucursalId },
    pause: !sucursalId,
    requestPolicy: 'network-only',
  });

  const [, updateLinea] = useMutation(ACTUALIZAR_LINEA_MUTATION);
  const [, setActiva] = useMutation(SET_LINEA_ACTIVA_MUTATION);
  const [, eliminar] = useMutation(ELIMINAR_LINEA_MUTATION);

  const lineas = useMemo(() => result.data?.lineas ?? [], [result.data]);
  const resumen = useMemo(() => {
    const activas = lineas.filter(linea => linea.activa).length;
    const conLimites = lineas.filter(linea => (
      linea.limite_kilos
      || linea.limite_bultos
      || linea.limite_valor_declarado
      || linea.limite_m3
      || linea.limite_rc
    )).length;

    return {
      total: lineas.length,
      activas,
      conLimites,
    };
  }, [lineas]);

  const onToggleActiva = async linea => {
    const response = await setActiva({ id: linea.id, sucursalId, activa: !linea.activa });
    if (response.error) {
      await Swal.fire('Error', response.error.message, 'error');
      return;
    }
    reexecute({ requestPolicy: 'network-only' });
  };

  const onDelete = async linea => {
    const confirm = await Swal.fire({
      title: `¿Eliminar línea ${linea.codigo}?`,
      text: 'Se hará eliminación lógica (soft delete).',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
    });
    if (!confirm.isConfirmed) return;

    const response = await eliminar({ id: linea.id, sucursalId });
    if (response.error) {
      await Swal.fire('Error', response.error.message, 'error');
      return;
    }
    await Swal.fire('Eliminada', 'La línea fue eliminada correctamente.', 'success');
    reexecute({ requestPolicy: 'network-only' });
  };

  const onSaveEdit = async payload => {
    const response = await updateLinea({
      id: editing.id,
      input: {
        ...payload,
        sucursal_id: sucursalId,
        localidad_id: editing.localidad_id ? Number(editing.localidad_id) : null,
      },
    });

    if (response.error) {
      await Swal.fire('Error al actualizar', response.error.message, 'error');
      return;
    }

    await Swal.fire('Actualizada', 'La línea se actualizó correctamente.', 'success');
    setEditing(null);
    reexecute({ requestPolicy: 'network-only' });
  };

  return (
    <div className="operator-shell space-y-3 px-4 pb-4 pt-2 md:px-5" data-qa-screen="lineas">
      <PageHeaderCompact
        title="Líneas"
        description="Catálogo operativo para armado de fojas y validación de carga."
        actions={(
          <Link
            to="/lineas/nueva"
            className="inline-flex h-9 items-center gap-2 rounded-xl bg-[rgb(var(--brand))] px-3 text-sm font-semibold text-white transition hover:bg-[rgb(var(--brand-700))]"
          >
            <Plus size={15} aria-hidden="true" />
            Nueva línea
          </Link>
        )}
      />

      {!sucursalId ? (
        <section className="rounded-2xl border border-[rgba(var(--border),0.58)] bg-[rgba(var(--panel),0.96)] p-4 shadow-[0_10px_26px_rgba(var(--shadow),0.08)]">
          <label className="flex max-w-sm flex-col gap-2 text-sm">
            <span className="font-semibold text-[rgb(var(--fg))]">Ingresá sucursalId para consultar líneas</span>
            <input
              value={fallbackSucursalId}
              onChange={e => setFallbackSucursalId(e.target.value)}
              className="h-10 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.94)] px-3 text-sm text-[rgb(var(--fg))] outline-none transition focus:ring-2 focus:ring-[rgb(var(--ring))]"
            />
          </label>
        </section>
      ) : null}

      <div className="grid gap-3 md:grid-cols-3">
        <MetricCardCompact label="Líneas activas" value={formatNumber(resumen.activas)} helper={`Total ${formatNumber(resumen.total)}`} icon={Route} tone="brand" />
        <MetricCardCompact label="Con límites" value={formatNumber(resumen.conLimites)} helper="Con reglas de capacidad definidas" icon={ShieldCheck} tone="success" />
        <MetricCardCompact label="Pendiente backend" value="Guías por línea" helper="Se mostrará cuando el backend entregue ese acumulado." icon={AlertTriangle} tone="warning" />
      </div>

      <section className="overflow-hidden rounded-2xl border border-[rgba(var(--border),0.58)] bg-[rgba(var(--panel),0.96)] shadow-[0_10px_26px_rgba(var(--shadow),0.08)]">
        <div className="flex items-center justify-between gap-3 border-b border-[rgba(var(--border),0.38)] px-4 py-2.5">
          <div>
            <h2 className="text-sm font-semibold text-[rgb(var(--fg))]">Listado operativo</h2>
            <p className="text-[12px] text-[rgba(var(--muted),0.88)]">
              {result.fetching ? 'Cargando líneas...' : `${formatNumber(lineas.length)} línea(s) disponibles`}
            </p>
          </div>
          {result.error ? <StatusChip label="Error de consulta" tone="danger" /> : null}
        </div>

        {result.fetching ? (
          <div className="px-4 py-6 text-sm text-[rgba(var(--muted),0.88)]">Cargando líneas...</div>
        ) : null}

        {result.error ? (
          <div className="px-4 py-4">
            <div className="rounded-2xl border border-[rgba(var(--danger),0.3)] bg-[rgba(var(--danger),0.1)] px-4 py-3 text-sm text-[rgb(var(--danger))]">
              {result.error.message}
            </div>
          </div>
        ) : null}

        {!result.fetching && !result.error && sucursalId && lineas.length === 0 ? (
          <div className="p-4">
            <EmptyState
              title="No hay líneas para esta sucursal"
              description="Creá una línea operativa para habilitar emisión de fojas y control de límites."
              action={(
                <Link
                  to="/lineas/nueva"
                  className="inline-flex h-10 items-center rounded-xl bg-[rgb(var(--brand))] px-4 text-sm font-semibold text-white"
                >
                  Crear línea
                </Link>
              )}
            />
          </div>
        ) : null}

        {lineas.length > 0 ? (
          <div className="overflow-auto">
            <table className="min-w-[980px] w-full text-left text-sm">
              <thead className="bg-[rgba(var(--subpanel),0.9)] text-[10px] uppercase tracking-[0.16em] text-[rgba(var(--muted),0.84)]">
                <tr>
                  <th className="px-3 py-2 font-semibold">Código</th>
                  <th className="px-3 py-2 font-semibold">Nombre</th>
                  <th className="px-3 py-2 font-semibold">Tramo / descripción</th>
                  <th className="px-3 py-2 font-semibold">Estado</th>
                  <th className="px-3 py-2 font-semibold">Límites</th>
                  <th className="px-3 py-2 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lineas.map(linea => (
                  <tr key={linea.id} className="border-t border-[rgba(var(--border),0.34)] align-top hover:bg-[rgba(var(--subpanel),0.42)]">
                    <td className="px-3 py-2.5">
                      <div className="font-mono text-[13px] font-semibold text-[rgb(var(--fg))]">{linea.codigo}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-[rgb(var(--fg))]">{linea.nombre}</div>
                    </td>
                    <td className="px-3 py-2.5 text-[13px] text-[rgba(var(--fg),0.74)]">
                      {linea.descripcion || 'Sin descripción operativa'}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusChip label={linea.activa ? 'Activa' : 'Inactiva'} tone={linea.activa ? 'success' : 'muted'} />
                    </td>
                    <td className="px-3 py-2.5">
                      <LimitChips linea={linea} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button type="button" onClick={() => setEditing(linea)} className="rounded-xl border border-[rgba(var(--border),0.6)] px-2.5 py-1.5 text-[12px] font-medium hover:bg-[rgba(var(--subpanel),0.72)]">Editar</button>
                        <button type="button" onClick={() => onToggleActiva(linea)} className="rounded-xl border border-[rgba(var(--border),0.6)] px-2.5 py-1.5 text-[12px] font-medium hover:bg-[rgba(var(--subpanel),0.72)]">{linea.activa ? 'Desactivar' : 'Activar'}</button>
                        <button type="button" onClick={() => onDelete(linea)} className="rounded-xl border border-[rgba(var(--danger),0.28)] px-2.5 py-1.5 text-[12px] font-medium text-[rgb(var(--danger))] hover:bg-[rgba(var(--danger),0.08)]">Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {editing ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(var(--shadow),0.35)] p-4">
          <div className="max-h-[92vh] w-full max-w-6xl overflow-auto rounded-2xl border border-[rgba(var(--border),0.58)] bg-[rgb(var(--bg))] p-4 shadow-[0_20px_60px_rgba(var(--shadow),0.2)]">
            <PageHeaderCompact title="Editar línea" description="Ajustá nombre, estado y límites sin salir del listado." />
            <LineaForm initialValues={editing} onSubmit={onSaveEdit} onCancel={() => setEditing(null)} submitText="Guardar cambios" />
          </div>
        </div>
      ) : null}
    </div>
  );
}
